import * as zod from 'zod'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { captureException } from '@sentry/react'
import { RestError } from '@lounge-fe/network'
import {
  addImageSizeParametersToUrl,
  Button,
  Form,
  Modal,
  Notification,
  Text,
  Divider,
} from '@lounge-fe/ui-kit'
import { getVariantFromOptions } from '../utils'
import { GiftCardRecipientProps, GiftCardRecipientValues } from './types'
import { addDays, datetimeToISODate } from '@/utils/date/date'
import { useRecentlyAddedProducts } from '@/features/cart/hooks/useRecentlyAddedProducts'
import { PhoneMockup } from '@/components/PhoneMockup'
import { useAddCartItem } from '@/features/cart/hooks/useAddCartItem'
import { useFormContext } from 'react-hook-form'

const recipientSchema = zod.object({
  recipientName: zod.string().nonempty(),
  recipientEmail: zod.string().email(),
  sendOn: zod.string().nonempty(),
  amount: zod.string().nonempty(),
  message: zod.string().optional(),
})

// Shopify imposes a limit of 90 days for sending gift cards
const maxSendOnDays = 90

export const GiftCardRecipient = ({
  fallbackImage,
  variants,
  selectedVariant = variants[0],
  options,
  title,
}: GiftCardRecipientProps) => {
  const { handleSubmit } = useFormContext()
  const [errors, setErrors] = useState<string[]>([])

  const { t } = useTranslation()
  const addCartItem = useAddCartItem({ useRecentlyAdded: false })
  const setRecentlyAddedProductIds = useRecentlyAddedProducts(
    (state) => state.setRecentlyAddedProductIds,
  )

  const today = datetimeToISODate(new Date())
  const maxDate = datetimeToISODate(addDays(maxSendOnDays, new Date()))

  const image = selectedVariant.image ?? fallbackImage

  const initialValues: GiftCardRecipientValues = {
    recipientName: '',
    recipientEmail: '',
    sendOn: today,
    amount: selectedVariant.options[options.position - 1],
  }

  const onSubmit = async ({
    recipientName,
    recipientEmail,
    message,
    sendOn,
    amount,
  }: GiftCardRecipientValues) => {
    setErrors([])

    const newOptions = selectedVariant
      ? [...selectedVariant.options]
      : variants[0].options

    newOptions[options.position - 1] = amount

    const variant = getVariantFromOptions(newOptions, variants)

    if (!variant) {
      // We should never get here, but report the error just in case
      setErrors([t('account.errors.unexpected')])
      captureException(
        new Error(
          `Couldn't find a variant for gift card with options: ${newOptions.join(
            ',',
          )}`,
        ),
      )
      return
    }

    try {
      await addCartItem.mutateAsync({
        id: variant.id,
        quantity: 1,
        properties: {
          'Recipient email': recipientEmail,
          'Recipient name': recipientName,
          Message: message,
          'Send on': sendOn,
          __shopify_send_gift_card_to_recipient: true,
          _giftcard_image: variant.image?.src,
        },
      })

      return variant
    } catch (error) {
      if (error instanceof RestError) {
        const errors = error.data.errors
        if (errors) {
          setErrors(Object.values(errors) ?? [t('account.errors.unexpected')])
        } else {
          setErrors([t('account.errors.unexpected')])
        }
      } else {
        t('account.errors.unexpected')
      }
      captureException(error)
      return
    }
  }

  return (
    <Modal>
      {({ onClose, onOpen }) => (
        <>
          <Button
            type="submit"
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleSubmit(onOpen)}
          >
            {t('product.gift_cards.send_as_gift')}
          </Button>
          <Modal.Content
            isMobileFullscreen
            size="2xl"
            className="overflow-y-auto"
          >
            <Form
              onSubmit={async (values) => {
                const addedVariant = await onSubmit(values)
                if (addedVariant) {
                  onClose()
                  setRecentlyAddedProductIds([addedVariant.id])
                }
              }}
              defaultValues={initialValues}
              validationSchema={recipientSchema}
            >
              {({ watch }) => {
                const amount = watch('amount')
                return (
                  <div className="grid md:grid-cols-5">
                    <div className="md:p-6 md:pr-2 md:col-span-2">
                      {image && (
                        <>
                          <div className="md:hidden flex justify-end my-3 mr-6">
                            <Modal.CloseButton className="!relative !top-0 !left-0 !right-0" />
                          </div>
                          <div className="max-md:h-[325px] h-full md:rounded-sm overflow-hidden bg-surface-secondary-default grid grid-rows-3 justify-center items-center relative w-full">
                            <div className="flex flex-1 justify-center items-center">
                              <Text variant="title-three">
                                {t('product.gift_cards.the_perfect_gift')}
                              </Text>
                            </div>
                            <div className="row-span-2 overflow-hidden h-full relative">
                              <div className="absolute left-0 top-0 right-0 bottom-0">
                                <PhoneMockup
                                  src={addImageSizeParametersToUrl({
                                    src: image.src,
                                    width: 480,
                                    height: 679,
                                    crop: 'center',
                                    onError: captureException,
                                  })}
                                  alt={image.alt}
                                />
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="md:col-span-3">
                      <Modal.Header className="sm:!px-0 max-sm:!pr-6">
                        <div className="flex justify-between items-center md:pt-8 sm:flex-col sm:justify-start sm:items-start">
                          <Text
                            variant="body-lg"
                            className="sm:typography--title-four font-regular sm:mb-2"
                          >
                            {title}
                          </Text>
                          <Text className="font-regular sm:font-regular">
                            {amount}
                          </Text>
                        </div>
                        <Modal.CloseButton className="max-md:hidden" />
                      </Modal.Header>
                      <Modal.Body>
                        <div className="grid sm:grid-cols-2 gap-6 mb-6">
                          {errors.length > 0 && (
                            <Notification
                              severity="critical"
                              className="sm:col-span-2"
                            >
                              {errors.map((error) => (
                                <Text key={error}>{error}</Text>
                              ))}
                            </Notification>
                          )}
                          <Form.Input
                            name="recipientName"
                            autoFocus
                            placeholder={t('product.gift_cards.recipient_name')}
                          />
                          <Form.Input
                            name="recipientEmail"
                            placeholder={t(
                              'product.gift_cards.recipient_email',
                            )}
                          />
                          <Form.Input
                            className="sm:col-span-2 [&_textarea]:resize-none"
                            rows={6}
                            name="message"
                            multiline
                            maxLength={250}
                            aria-label={t('product.gift_cards.message')}
                            placeholder={t('product.gift_cards.message')}
                          />
                          <Form.Input
                            type="date"
                            name="sendOn"
                            labelPosition="outside"
                            min={today}
                            max={maxDate}
                            label={t('product.gift_cards.date_to_receive')}
                          />
                          <Form.Select
                            name="amount"
                            options={options.values.map((value) => ({
                              label: value,
                              value,
                            }))}
                            label={t('product.select_amount')}
                          />
                        </div>
                      </Modal.Body>
                      <Modal.Actions className="md:col-span-2 sticky bottom-0 border-t pt-6 md:mx-6 md:px-0 bg-white">
                        <Button variant="outline" onClick={onClose}>
                          {t('action.cancel')}
                        </Button>
                        <Form.Submit>{t('product.add_to_bag')}</Form.Submit>
                      </Modal.Actions>
                    </div>
                  </div>
                )
              }}
            </Form>
          </Modal.Content>
        </>
      )}
    </Modal>
  )
}
